import {
    clearEmailCampaignError,
    createEmailCampaignThunk,
    deleteEmailCampaignThunk,
    fetchEmailCampaignCountersThunk,
    fetchEmailCampaignCustomersThunk,
    fetchEmailCampaignQuoteThunk,
    fetchEmailCampaignThunk,
    fetchEmailMarketingSenderThunk,
    fetchEmailTemplatesThunk,
    resetEmailCampaign,
    resetEmailCampaignQuote,
    updateEmailCampaignThunk
} from '@spike/email-marketing-action';
import { EmailCampaign, EmailCampaignPopulation } from '@spike/email-marketing-model';
import EmailSenderComponent from 'components/EmailMarketing/EmailSender/EmailSenderComponent';
import { Drawer } from 'components/UI/V3/Drawer/Drawer';
import { DrawerBody } from 'components/UI/V3/Drawer/Body/DrawerBody';
import { DrawerHeader } from 'components/UI/V3/Drawer/Header/DrawerHeader';
import {
    FullWindowModal,
    FullWindowModalBody,
    FullWindowModalCloseButton,
    FullWindowModalHeader
} from 'components/UI/V3/Modals/FullWindowModal';
import Spinner from 'components/UI/V3/Spinner/Spinner';
import { useApiClientWrapper, useMarketplace, useMasterData } from 'hooks';
import { useDebounce } from 'hooks/useDebounce';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useMediaQuery } from 'react-responsive';
import { RootState } from 'store';

import CampaignSubmittedScreen from './components/CampaignSubmittedScreen';
import { EmailCampaignWizardHeader } from './components/EmailCampaignWizardHeader';
import { MobileWizardFooter } from './components/MobileWizardFooter';
import SendConfirmationModal from './components/SendConfirmationModal';
import WizardExitModal from './components/WizardExitModal';
import { formatCampaignNameError } from './formatCampaignNameError';
import { getRecipientCount, RecipientCountInputs } from './getRecipientCount';
import { CampaignNameStep } from './steps/CampaignNameStep';
import { RecipientsStep } from './steps/RecipientsStep';
import { ReviewAndSendStep } from './steps/ReviewAndSendStep';
import { TemplateStep } from './steps/TemplateStep';
import { EmailCampaignFormValues, EmailCampaignRecipientType } from './types';
import moment from 'moment';

import styles from './AddEmailCampaign.module.scss';

interface AddEmailCampaignProps {
    onClose: () => void;
    /**
     * When provided, the wizard fetches that draft and resumes editing instead
     * of starting a new draft. The user is dropped onto the first incomplete
     * step (see `computeInitialStepFromDraft`). Used by the Drafts list flow.
     */
    initialCampaignId?: number;
}

/**
 * Derive the backend `population` value the campaign should be persisted with
 * given the wizard's `recipientType`. Single source of truth for the mapping:
 *
 *   all_profiles  → both
 *   all_clients   → customers
 *   client_groups → customers  (tags only exist for clients)
 *   leads         → leads
 */
export const populationFor = (recipientType: EmailCampaignRecipientType): EmailCampaignPopulation => {
    if (recipientType === 'all_profiles') return 'both';
    if (recipientType === 'leads') return 'leads';
    return 'customers';
};

/**
 * Reverse mapping used when reopening a draft — reconstructs the wizard's
 * `recipientType` from the persisted `(population, criteria)` pair.
 *
 *   by_tag (any population) → client_groups   (see note below)
 *   population = both       → all_profiles
 *   population = leads      → leads
 *   otherwise               → all_clients
 *
 * `by_tag` wins over the population field because tags only exist for
 * clients — a `by_tag` draft is always a Client Groups campaign in the
 * current model. This also correctly handles legacy drafts saved before
 * CUD-5132 shipped, where `population` defaults to `both` on the wire but
 * `criteria='by_tag'` was still the user's original intent.
 *
 * When the reconstruction changes the effective population (e.g. legacy
 * both+by_tag → customers via client_groups), the next Continue persists
 * the new population; the backend auto-reconciles materialised recipients
 * with the new pool (see BE ticket CUD-5133).
 *
 * Anything unrecognised (maqueta criteria like `by_pet_type` in legacy
 * drafts) falls back to `all_clients` — the safe default that preserves
 * the "clients" population but drops the maqueta filter the current UI
 * can't render.
 */
const recipientTypeFromDraft = (
    population: EmailCampaignPopulation,
    criteria: string | null
): EmailCampaignRecipientType => {
    if (criteria === 'by_tag') return 'client_groups';
    if (population === 'leads') return 'leads';
    if (population === 'both') return 'all_profiles';
    return 'all_clients';
};

/**
 * Map a loaded draft → wizard form values. Fields the user has not filled yet
 * stay at their defaults so the corresponding step shows blank inputs.
 *
 * `businessName` is used as the fallback for the Sender Name when the draft
 * has no `fromName` set (never edited by the user) — matches the initial
 * state prefill so resuming a draft behaves the same as starting fresh.
 */
const valuesFromDraft = (draft: EmailCampaign, businessName: string): EmailCampaignFormValues => {
    // Backend defaults `population` to `"both"` for legacy drafts saved before
    // CUD-5132 shipped — the converter already normalises undefined → "both".
    const recipientType = recipientTypeFromDraft(draft.population ?? 'both', draft.criteria);

    // Tag ids for `client_groups` come back as a comma-separated string in
    // `criteria_args`. Only relevant when the reconstructed recipientType is
    // `client_groups`; other modes ignore it.
    const args = draft.criteriaArgs ?? '';
    const selectedTagIds: string[] = recipientType === 'client_groups' && args ? args.split(',') : [];

    // Reconstruct the picker's include/exclude state from the persisted
    // `recipient_selection` (BE ticket CUD-5135). Legacy drafts saved before
    // that ticket shipped omit the field — default to exclude mode with an
    // empty exclusion list, matching the pre-feature behavior.
    const selection = draft.recipientSelection;
    let selectedClientIds: number[] | 'all';
    let excludedClientIds: number[];
    if (selection?.mode === 'include') {
        selectedClientIds = selection.customerIds;
        excludedClientIds = [];
    } else if (selection?.mode === 'exclude') {
        selectedClientIds = 'all';
        excludedClientIds = selection.excludeIds;
    } else {
        // Legacy draft — no recipient_selection on the wire.
        selectedClientIds = 'all';
        excludedClientIds = [];
    }

    return {
        campaignName: draft.name ?? '',
        recipientType,
        selectedTagIds,
        excludedClientIds,
        selectedClientIds,
        subject: draft.subject ?? '',
        previewText: draft.previewText ?? '',
        // `||` (not `??`) so a persisted empty string also falls back to the
        // business name — the wizard's validation blocks Continue on empty,
        // so an empty stored value is effectively "never set" from the user's
        // perspective.
        senderName: draft.fromName || businessName || '',
        senderEmail: draft.fromEmail ?? '',
        emailTemplateId: draft.emailTemplateId,
        // Step 3 fields live in wizard memory only — backend doesn't yet store
        // them. Resuming a draft always starts at Send Now.
        sendTime: 'now',
        scheduledDate: null,
        scheduledTime: null,
        campaignId: typeof draft.id === 'number' ? draft.id : null
    };
};

/**
 * Combine wizard scheduled date+time into a moment and check that it sits in
 * the future. Used by `isStepValidFor` for Step 3 — Schedule mode requires a
 * future timestamp to enable the Continue/Schedule button.
 */
const isFutureScheduledDateTime = (date: string | null, time: string | null): boolean => {
    if (!date || !time) return false;
    const combined = moment(`${date} ${time}`, 'YYYY-MM-DD HH:mm', true);
    if (!combined.isValid()) return false;
    return combined.isAfter(moment());
};

/**
 * Land the user on the first step whose fields are NOT yet filled — the step
 * they would naturally continue from. A draft that already has a template is
 * resumed at Review & Send (step 3).
 */
const computeInitialStepFromDraft = (draft: EmailCampaign): number => {
    if (draft.emailTemplateId !== null) return 3;
    if (draft.criteria !== null && draft.criteria !== '') return 2;
    if ((draft.name ?? '').trim().length > 0) return 1;
    return 0;
};

/**
 * Computes whether a given step's form is ready for Continue. Centralised here
 * (rather than per-step) so the wizard header's disabled state stays in sync
 * with the step the user is on, and so the persistence calls in handleContinue
 * always run against valid data.
 */
const isStepValidFor = (
    step: number,
    values: EmailCampaignFormValues,
    context: Pick<RecipientCountInputs, 'counters' | 'fetchedCustomersTotal'>
): boolean => {
    if (step === 0) {
        return values.campaignName.trim().length > 0;
    }
    if (step === 1) {
        // Gate on the effective recipient count — one check that covers all
        // shapes (All Clients / by_tag single / by_tag multi / include mode)
        // plus the "0 recipients after exclusions" cases. `null` means the
        // count is not yet computable (counters loading, or maqueta criteria
        // the BE doesn't resolve) → keep Continue disabled.
        const count = getRecipientCount({ values, ...context });
        return count !== null && count > 0;
    }
    if (step === 2) {
        // `values.senderEmail` is no longer user-editable — the wizard reads
        // the effective sender from `state.emailMarketingSender` (verified
        // custom sender OR Cuddles default). It still lives in form values
        // for backwards-compat on draft resume, but it doesn't gate Continue.
        return (
            values.subject.trim().length > 0 && values.senderName.trim().length > 0 && values.emailTemplateId !== null
        );
    }
    if (step === 3) {
        // Send Now: always valid (header button just opens the modal).
        // Schedule: requires a date+time that's still in the future at
        // validation time. We re-check on submit too so a stale time doesn't
        // sneak through if the user lingers on the step.
        if (values.sendTime === 'now') return true;
        return isFutureScheduledDateTime(values.scheduledDate, values.scheduledTime);
    }
    return true;
};

export const AddEmailCampaign: React.FC<AddEmailCampaignProps> = ({ onClose, initialCampaignId }) => {
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();
    const marketplace = useMarketplace();
    const { emailCampaignsBillingEnabled } = useMasterData();
    const saving = useSelector((state: RootState) => state.emailCampaigns.saving);
    const validationErrors = useSelector((state: RootState) => state.emailCampaigns.validationErrors);

    // Formatted error for the campaign name — surfaces backend-rejected names
    // (e.g. duplicate) in both the step 0 form and the wizard header's inline
    // editable title (step 1+). `CampaignNameStep` reads the same Redux slice
    // via its own selector; both render the same string via the shared
    // `formatCampaignNameError` util.
    const nameError =
        validationErrors?.name && validationErrors.name.length > 0
            ? formatCampaignNameError(validationErrors.name)
            : null;

    const isResumingDraft = typeof initialCampaignId === 'number';
    const [currentStep, setCurrentStep] = React.useState(0);
    /**
     * The furthest step the user has reached so far. Drives the clickable
     * range of the wizard stepper — any step <= this can be jumped to.
     *
     * - New campaign: starts at 0, bumped each time a Continue advances.
     * - Resumed draft: initialised to the computed initial step (matches
     *   currentStep on hydration so all data-bearing steps are clickable).
     */
    const [maxReachedStep, setMaxReachedStep] = React.useState<number>(0);
    const [showExitPrompt, setShowExitPrompt] = React.useState(false);
    // While a draft is being fetched on resume, suppress the wizard UI so the
    // user doesn't see a flash of empty fields before the prefill.
    const [hydrating, setHydrating] = React.useState<boolean>(isResumingDraft);
    const isMobile = useMediaQuery({ query: '(max-width:600px)' });

    const [values, setValues] = React.useState<EmailCampaignFormValues>({
        campaignName: '',
        // Default to `all_clients` — the most common intent (send to your
        // existing client base). `populationFor('all_clients') === 'customers'`
        // so the initial counters fetch scopes to just clients. Businesses
        // that want everyone (clients + leads) pick `All Profiles` explicitly.
        recipientType: 'all_clients',
        selectedTagIds: [],
        excludedClientIds: [],
        // Default to `'all'` = exclude mode (matches the legacy behaviour the
        // wizard had before include mode existed). The drawer flips this to
        // an array when the user toggles off Select All.
        selectedClientIds: 'all',
        subject: '',
        previewText: '',
        // Prefill with the marketplace's business name (SU-2300). Seeding at
        // wizard-state init — instead of in a Step 2 useEffect — is what
        // actually makes it stick: the V3 <Input> in TemplateStepForm caches
        // its initial value in useState and does not re-sync from later
        // prop updates. User edits flow through onChange normally.
        senderName: marketplace.basics.businessName ?? '',
        senderEmail: '',
        emailTemplateId: null,
        sendTime: 'now',
        scheduledDate: null,
        scheduledTime: null,
        campaignId: null
    });

    // ----------------------------------------------------------------------
    // Sender drawer — lifted to the wizard root so both step 2 (TemplateStep)
    // and step 3 (SendConfirmationModal) can open the same instance. The
    // EmailSenderComponent is dual-mode; we pass `embedded` + `onVerified` /
    // `onExit` so the drawer chrome owns padding/title and auto-closes when
    // the user finishes verifying (or bails).
    // ----------------------------------------------------------------------
    const [showSenderDrawer, setShowSenderDrawer] = React.useState<boolean>(false);
    const handleOpenSenderDrawer = React.useCallback(() => setShowSenderDrawer(true), []);
    const handleCloseSenderDrawer = React.useCallback(() => setShowSenderDrawer(false), []);

    // ----------------------------------------------------------------------
    // Step 3 — Review & Send commit flow
    //
    // Two terminal states the wizard can reach from Step 3:
    //   - `showConfirmModal` is true while the user is reviewing the modal
    //     (Adyen drop-in for Send Now, plain Schedule button for mock).
    //   - `submittedAs` is set once the user commits — the wizard layout is
    //     replaced by `CampaignSubmittedScreen` and remains there until the
    //     user closes ("Go to Email Campaigns").
    // ----------------------------------------------------------------------
    const [showConfirmModal, setShowConfirmModal] = React.useState<boolean>(false);
    const [submittedAs, setSubmittedAs] = React.useState<'sent' | 'scheduled' | null>(null);

    /**
     * Recipient total used for the modal's quote refresh + summary copy.
     * Mirrors the math `TotalClientsBar` runs in Step 1; here we pull
     * directly from Redux because that step's local state isn't lifted up.
     */
    const counters = useSelector((state: RootState) => state.emailCampaigns.counters);
    const fetchedCustomersTotal = useSelector((state: RootState) => state.emailCampaigns.customers.totalRecords);
    // Delegates to the shared `getRecipientCount` helper — same source of
    // truth used by `TotalClientsBar` and Step 1 Continue validation. `null`
    // (not yet computable) coerces to 0 for downstream consumers (quote fetch
    // treats 0 as "no request").
    const recipientTotal = React.useMemo<number>(
        () => getRecipientCount({ values, counters, fetchedCustomersTotal }) ?? 0,
        [values, counters, fetchedCustomersTotal]
    );

    // ----------------------------------------------------------------------
    // Data fetching — owned by the wizard root so it stays warm regardless of
    // which step the user lands on. Matters most on draft resume, which can
    // jump straight to step 3 without ever mounting RecipientsStep; before
    // this lived inside that step and the Review summary rendered "0 Clients"
    // because `counters` was still null when step 3 mounted.
    // ----------------------------------------------------------------------

    // Counter snapshot — re-fetches whenever the recipient mode changes
    // (which drives the population, see `populationFor`) so the bar and
    // the "Edit clients" drawer reflect only the selected pool. Marketplace
    // customer data itself isn't expected to mutate mid-wizard, so we
    // don't refetch on other triggers.
    React.useEffect(() => {
        dispatch(
            fetchEmailCampaignCountersThunk(apiClientWrapper, '30,60,90,180', populationFor(values.recipientType))
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [values.recipientType]);

    // Email templates (system + own) — fetched once at wizard root so step 3
    // can resolve `emailTemplateId` to a template object on draft resume,
    // even when the user lands there without ever mounting TemplateStep.
    // No `scope` param → BE returns both system seeds and the marketplace's
    // own templates, matching what `EmailTemplatesGallery` already does.
    React.useEffect(() => {
        dispatch(fetchEmailTemplatesThunk(apiClientWrapper));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Marketplace email sender — fetched once at wizard root so step 2 can
    // render the "Sending from" section + step 3 can gate Send when sender
    // is `enabled && !verified`. The GET endpoint auto-refreshes pending
    // status BE-side, so re-mounts of the tab pick up fresh state.
    React.useEffect(() => {
        dispatch(fetchEmailMarketingSenderThunk(apiClientWrapper));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Multi-tag union — debounced because the counters endpoint only returns
    // per-tag totals; summing them would over-count clients with multiple
    // tags. We derive the exact union via the customers endpoint with size=1
    // and read `totalRecords` from the slice.
    const debouncedTagIds = useDebounce(values.selectedTagIds, 300);
    const isMultiTagMode = values.recipientType === 'client_groups' && debouncedTagIds.length > 1;

    React.useEffect(() => {
        if (!isMultiTagMode) return;
        dispatch(
            fetchEmailCampaignCustomersThunk(apiClientWrapper, {
                criteria: 'by_tag',
                args: debouncedTagIds.join(','),
                page: 1,
                size: 1,
                population: populationFor(values.recipientType)
            })
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedTagIds, isMultiTagMode, values.recipientType]);

    // Live pricing quote — debounced on the resolved recipient total. Cleared
    // via `resetEmailCampaignQuote` when total drops to 0 so the cost line in
    // Step 1 + the modal don't render stale figures while criteria are blank.
    //
    // Skipped entirely when billing is disabled (CUD-4799 MVP free-send mode):
    // no price line in step 1, no Price section in the modal, so the quote
    // would be a dead network call.
    const debouncedRecipientTotal = useDebounce(recipientTotal, 300);

    React.useEffect(() => {
        if (!emailCampaignsBillingEnabled) return;
        if (debouncedRecipientTotal === 0) {
            dispatch(resetEmailCampaignQuote());
            return;
        }
        dispatch(fetchEmailCampaignQuoteThunk(apiClientWrapper, debouncedRecipientTotal));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedRecipientTotal]);

    // Drop transient slice state on unmount — currentCampaign + validationErrors
    // should not leak between separate wizard sessions.
    React.useEffect(() => {
        return () => {
            dispatch(resetEmailCampaign());
        };
    }, [dispatch]);

    // Fetch + prefill when resuming a draft from the Drafts list. If the fetch
    // fails (campaign deleted, network error, etc.) the wizard closes — the
    // thunk's alertErrorHandler shows the toast.
    React.useEffect(() => {
        if (typeof initialCampaignId !== 'number') return;

        let cancelled = false;
        const run = async () => {
            const draft = await (dispatch(
                fetchEmailCampaignThunk(apiClientWrapper, initialCampaignId)
            ) as unknown as Promise<EmailCampaign | null>);

            if (cancelled) return;
            if (!draft) {
                onClose();
                return;
            }

            const initial = computeInitialStepFromDraft(draft);
            setValues(valuesFromDraft(draft, marketplace.basics.businessName ?? ''));
            setCurrentStep(initial);
            setMaxReachedStep(initial);
            setHydrating(false);
        };

        run();

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialCampaignId]);

    const isCampaignNameStep = currentStep === 0;
    const isStepValid = isStepValidFor(currentStep, values, { counters, fetchedCustomersTotal });
    const continueDisabled = !isStepValid || saving;

    const handleChange = React.useCallback((nextValues: Partial<EmailCampaignFormValues>) => {
        setValues(previousValues => ({
            ...previousValues,
            ...nextValues
        }));
    }, []);

    /**
     * Commit a new campaign name from the wizard header's inline editable
     * title. Fires only on Enter / blur (the `InlineEditableTitle` swallows
     * keystroke noise), so each invocation is one persistence call — no
     * debounce needed.
     *
     * Different from step 0's `Input.onChange`, which only updates local form
     * state and defers persistence to Continue → `persistStep(0)`. The inline
     * edit on step 1+ has no Continue gating, so persistence happens here.
     *
     * If the campaign hasn't been created yet (campaignId is null) we just
     * update local state — backend persistence will land on the next Continue.
     * In practice this branch is unreachable from step 1+ because step 0's
     * Continue is what creates the campaign, but it's a cheap defensive guard.
     */
    const handleCommitCampaignName = React.useCallback(
        (next: string) => {
            const trimmed = next.trim();
            if (trimmed.length === 0) return;
            if (validationErrors) {
                dispatch(clearEmailCampaignError());
            }
            setValues(previousValues => ({ ...previousValues, campaignName: trimmed }));
            if (values.campaignId !== null) {
                dispatch(updateEmailCampaignThunk(apiClientWrapper, values.campaignId, { name: trimmed }));
            }
        },
        [apiClientWrapper, dispatch, validationErrors, values.campaignId]
    );

    /**
     * Persists a step's form values to the backend draft. Returns `true` on
     * success, `false` when the call failed (toast / inline error already
     * surfaced by the thunk) or when there's nothing valid to persist yet
     * (e.g. step 2 without an email template).
     *
     * Used by both `handleContinue` (advance one step) and `handleStepperClick`
     * (forward-jump via the stepper) — the persistence semantics are identical.
     */
    const persistStep = React.useCallback(
        async (step: number): Promise<boolean> => {
            if (step === 0) {
                if (values.campaignId === null) {
                    // Include population on create so the counters endpoint and
                    // the picker scope correctly from the very first fetch.
                    const result = await (dispatch(
                        createEmailCampaignThunk(apiClientWrapper, {
                            name: values.campaignName.trim(),
                            population: populationFor(values.recipientType)
                        })
                    ) as unknown as Promise<{ id?: number } | null>);
                    if (!result || typeof result.id !== 'number') return false;
                    setValues(previousValues => ({ ...previousValues, campaignId: result.id ?? null }));
                    return true;
                }
                const result = await (dispatch(
                    updateEmailCampaignThunk(apiClientWrapper, values.campaignId, {
                        name: values.campaignName.trim()
                    })
                ) as unknown as Promise<unknown>);
                return Boolean(result);
            }

            if (step === 1) {
                if (values.campaignId === null) return false;

                const population = populationFor(values.recipientType);

                // Include mode (positive selection from the drawer) bypasses
                // `criteria` entirely. The BE's `resolve_customer_ids` branch
                // only fires when `criteria` is present in the payload, so
                // sending `customerIds` alone leaves the materialised
                // recipients exactly equal to the selected list.
                if (Array.isArray(values.selectedClientIds)) {
                    const result = await (dispatch(
                        updateEmailCampaignThunk(apiClientWrapper, values.campaignId, {
                            customerIds: values.selectedClientIds,
                            population
                        })
                    ) as unknown as Promise<unknown>);
                    return Boolean(result);
                }

                // Exclude mode. Only `client_groups` sends `by_tag`; every
                // other mode collapses to `criteria = "all"` and lets the
                // population param on the same request scope the pool.
                // `population` is always sent — the BE auto-reconciles
                // materialised recipients if it changed between saves.
                const updatePayload =
                    values.recipientType === 'client_groups'
                        ? {
                              criteria: 'by_tag',
                              criteriaArgs: values.selectedTagIds.join(','),
                              excludeIds: values.excludedClientIds,
                              population
                          }
                        : {
                              criteria: 'all',
                              excludeIds: values.excludedClientIds,
                              population
                          };

                const result = await (dispatch(
                    updateEmailCampaignThunk(apiClientWrapper, values.campaignId, updatePayload)
                ) as unknown as Promise<unknown>);
                return Boolean(result);
            }

            if (step === 2) {
                if (values.campaignId === null || values.emailTemplateId === null) return false;
                const result = await (dispatch(
                    updateEmailCampaignThunk(apiClientWrapper, values.campaignId, {
                        subject: values.subject.trim(),
                        fromName: values.senderName.trim(),
                        fromEmail: values.senderEmail.trim(),
                        previewText: values.previewText.trim(),
                        emailTemplateId: values.emailTemplateId
                    })
                ) as unknown as Promise<unknown>);
                return Boolean(result);
            }

            // Step 3 (Review & Send) has no form data to persist yet — it
            // becomes a no-op until the review/send flow is wired.
            return true;
        },
        [apiClientWrapper, dispatch, values]
    );

    /**
     * Advance one step from the current one. Persists the current step before
     * incrementing; the step counter and `maxReachedStep` update on success.
     *
     * Step 3 is special: the header "Continue" button is relabeled to
     * "Send" / "Schedule" and opens the confirmation modal instead of
     * advancing to a (non-existent) step 4.
     */
    const handleContinue = React.useCallback(async () => {
        if (continueDisabled) return;
        if (currentStep === 3) {
            setShowConfirmModal(true);
            return;
        }
        const ok = await persistStep(currentStep);
        if (!ok) return;
        const next = Math.min(3, currentStep + 1);
        setCurrentStep(next);
        setMaxReachedStep(previous => Math.max(previous, next));
    }, [continueDisabled, currentStep, persistStep]);

    /**
     * Step 3 secondary action — persist whatever is currently on the form and
     * close the wizard, leaving the campaign as `draft` in BE. Same shape as
     * `persistStep(2)` because Step 3 itself has no persistable fields today
     * (Send Time lives in wizard memory only).
     */
    const handleSaveAsDraft = React.useCallback(async () => {
        if (saving) return;
        const ok = await persistStep(2);
        if (ok) onClose();
    }, [persistStep, saving, onClose]);

    /**
     * Jump to a specific step via the wizard stepper. Allowed only when the
     * target has been reached at least once before (tracked via
     * `maxReachedStep`).
     *
     *   - Backward: navigate immediately, no save (mirrors the Back button).
     *   - Forward: refuse if the current step's form is invalid; otherwise
     *     persist it before navigating so backend stays in sync with what the
     *     user has filled in locally.
     */
    const handleStepperClick = React.useCallback(
        async (target: number) => {
            if (saving) return;
            if (target === currentStep) return;
            if (target > maxReachedStep) return;

            if (target > currentStep) {
                if (!isStepValidFor(currentStep, values, { counters, fetchedCustomersTotal })) return;
                const ok = await persistStep(currentStep);
                if (!ok) return;
            }

            setCurrentStep(target);
        },
        [counters, currentStep, fetchedCustomersTotal, maxReachedStep, persistStep, saving, values]
    );

    const handleBack = React.useCallback(() => {
        setCurrentStep(previousStep => Math.max(0, previousStep - 1));
    }, []);

    // ----------------------------------------------------------------------
    // Cancel / exit flow
    // ----------------------------------------------------------------------

    const handleCloseRequest = React.useCallback(() => {
        // If no draft exists yet, the X just closes — nothing to confirm.
        if (values.campaignId === null) {
            onClose();
            return;
        }
        setShowExitPrompt(true);
    }, [onClose, values.campaignId]);

    const handleLeave = React.useCallback(() => {
        setShowExitPrompt(false);
        onClose();
    }, [onClose]);

    const handleKeepEditing = React.useCallback(() => {
        setShowExitPrompt(false);
    }, []);

    if (hydrating) {
        return (
            <FullWindowModal>
                <FullWindowModalBody>
                    <div className={styles.hydratingState}>
                        <Spinner />
                    </div>
                </FullWindowModalBody>
            </FullWindowModal>
        );
    }

    // Terminal state — wizard has been committed. Replace the full layout
    // with the success screen; the X / CTA there exit through `onClose`.
    if (submittedAs !== null) {
        return (
            <FullWindowModal>
                <FullWindowModalBody>
                    <CampaignSubmittedScreen
                        campaignName={values.campaignName}
                        mode={submittedAs}
                        onClose={onClose}
                    />
                </FullWindowModalBody>
            </FullWindowModal>
        );
    }

    const continueLabel = currentStep === 3 ? (values.sendTime === 'now' ? 'Send' : 'Schedule') : 'Continue';

    return (
        <FullWindowModal>
            {showExitPrompt && (
                <WizardExitModal
                    onLeave={handleLeave}
                    onKeepEditing={handleKeepEditing}
                />
            )}

            {showConfirmModal && (
                <SendConfirmationModal
                    values={values}
                    recipientTotal={recipientTotal}
                    onCancel={() => setShowConfirmModal(false)}
                    onSubmitted={mode => {
                        setShowConfirmModal(false);
                        setSubmittedAs(mode);
                    }}
                    onOpenSenderDrawer={handleOpenSenderDrawer}
                />
            )}

            {currentStep === 0 && (
                <FullWindowModalHeader>
                    <div className={styles.step0HeaderLayout}>
                        <img
                            src="/images/logo/logo.png"
                            alt="Logo"
                            className={styles.step0Logo}
                        />
                        <FullWindowModalCloseButton onClick={handleCloseRequest} />
                    </div>
                </FullWindowModalHeader>
            )}

            <FullWindowModalBody>
                {currentStep > 0 && (
                    <EmailCampaignWizardHeader
                        currentStep={currentStep}
                        maxReachedStep={maxReachedStep}
                        onClose={handleCloseRequest}
                        onContinue={handleContinue}
                        onBack={handleBack}
                        onStepClick={handleStepperClick}
                        hideContinueButton={isMobile}
                        continueDisabled={continueDisabled}
                        continueLabel={continueLabel}
                        onSaveAsDraft={currentStep === 3 ? handleSaveAsDraft : undefined}
                        saveAsDraftDisabled={saving}
                        campaignName={values.campaignName}
                        onCommitCampaignName={handleCommitCampaignName}
                        nameError={nameError}
                    />
                )}

                {currentStep === 0 && (
                    <CampaignNameStep
                        values={values}
                        onChange={handleChange}
                        onContinue={handleContinue}
                        hideContinueButton={isMobile}
                        continueDisabled={continueDisabled}
                    />
                )}

                {currentStep === 1 && (
                    <RecipientsStep
                        values={values}
                        onChange={handleChange}
                        onContinue={handleContinue}
                    />
                )}

                {currentStep === 2 && (
                    <TemplateStep
                        values={values}
                        onChange={handleChange}
                        onContinue={handleContinue}
                        onOpenSenderDrawer={handleOpenSenderDrawer}
                    />
                )}

                {currentStep === 3 && (
                    <ReviewAndSendStep
                        values={values}
                        onChange={handleChange}
                        onContinue={handleContinue}
                        recipientTotal={recipientTotal}
                        onOpenSenderDrawer={handleOpenSenderDrawer}
                    />
                )}

                {isMobile && (
                    <MobileWizardFooter
                        label={continueLabel}
                        disabled={isCampaignNameStep ? continueDisabled : continueDisabled}
                        onClick={handleContinue}
                    />
                )}
            </FullWindowModalBody>

            {showSenderDrawer && (
                <Drawer onClose={handleCloseSenderDrawer}>
                    <DrawerHeader title="Email Sender" />
                    <DrawerBody>
                        <EmailSenderComponent
                            embedded
                            onVerified={handleCloseSenderDrawer}
                            onExit={handleCloseSenderDrawer}
                        />
                    </DrawerBody>
                </Drawer>
            )}
        </FullWindowModal>
    );
};
